import React, { useState, useEffect, useCallback } from "react";
import {
    ShieldCheck,
    AlertCircle,
    XCircle,
    HelpCircle,
    Sparkles,
    Loader2,
    Award,
    RefreshCw,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    runTenderSolvencyCheck,
    getTenderSolvencyCheck,
} from "@/lib/api";

const STATUS_META = {
    ok: {
        label: "Cumples",
        cls: "text-emerald-700 bg-emerald-50 border-emerald-200",
        gradient: "from-emerald-500 to-teal-600",
        icon: ShieldCheck,
        dot: "bg-emerald-500",
    },
    warn: {
        label: "Justo",
        cls: "text-amber-700 bg-amber-50 border-amber-200",
        gradient: "from-amber-500 to-orange-600",
        icon: AlertCircle,
        dot: "bg-amber-500",
    },
    fail: {
        label: "No cumples",
        cls: "text-red-700 bg-red-50 border-red-200",
        gradient: "from-red-500 to-rose-600",
        icon: XCircle,
        dot: "bg-red-500",
    },
    "n/a": {
        label: "Sin requisito",
        cls: "text-ink-500 bg-ink-50 border-ink-200",
        gradient: "from-slate-400 to-slate-500",
        icon: HelpCircle,
        dot: "bg-slate-300",
    },
};

const metaOf = (s) => STATUS_META[s] || STATUS_META["n/a"];

export function SolvencyMatchCard({ tenderId, tender }) {
    const [result, setResult] = useState(tender?.solvency_check || null);
    const [running, setRunning] = useState(false);

    // Hydrate from server in case the cached value isn't in tender prop
    useEffect(() => {
        if (result) return;
        getTenderSolvencyCheck(tenderId)
            .then((r) => {
                if (r) setResult(r);
            })
            .catch(() => {});
    }, [tenderId, result]);

    const run = useCallback(async () => {
        setRunning(true);
        try {
            const r = await runTenderSolvencyCheck(tenderId);
            setResult(r);
            const ov = r?.overall;
            if (ov === "ok") {
                toast.success("Cumples los requisitos de solvencia");
            } else if (ov === "warn") {
                toast.warning("Cumples justo — revisa los detalles");
            } else if (ov === "fail") {
                toast.error("Te falta acreditar algo de solvencia");
            } else {
                toast.info("Sin requisitos de solvencia detectados");
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    "No se pudo evaluar la solvencia (¿tienes documentos en /app/solvencia?)"
            );
        } finally {
            setRunning(false);
        }
    }, [tenderId]);

    // Surface state — no result yet
    if (!result) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-5 shadow-sm"
                data-testid="solvency-match-card"
            >
                <div className="flex items-start gap-4">
                    <div className="shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-pop">
                        <Award size={22} strokeWidth={2.5} />
                    </div>
                    <div className="flex-1">
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700 mb-1">
                            Comprobación de solvencia · LCSP Art. 87-90
                        </div>
                        <div className="font-display text-xl font-black text-ink-950 leading-tight mb-1">
                            ¿Cumples los requisitos de esta licitación?
                        </div>
                        <p className="text-sm text-ink-600 font-medium mb-3">
                            La IA compara los requisitos del pliego con tu perfil de solvencia
                            acumulado en LicitaIA y te dice si encajas, si te falta algo, o si
                            estás justo.
                        </p>
                        <Button
                            onClick={run}
                            disabled={running}
                            className="rounded-full bg-amber-600 hover:bg-amber-700 text-white font-bold h-10 px-5 gap-1.5 shadow-pop"
                            data-testid="solvency-match-run"
                        >
                            {running ? (
                                <>
                                    <Loader2 size={14} className="animate-spin" />
                                    Evaluando con IA…
                                </>
                            ) : (
                                <>
                                    <Sparkles size={14} />
                                    Comprobar mi solvencia
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </motion.div>
        );
    }

    const oMeta = metaOf(result.overall);
    const OIcon = oMeta.icon;

    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-ink-100 rounded-2xl shadow-pop overflow-hidden"
            data-testid="solvency-match-card"
        >
            {/* Header banner */}
            <div className={`bg-gradient-to-r ${oMeta.gradient} text-white p-5`}>
                <div className="flex items-start gap-4">
                    <div className="shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/20 backdrop-blur">
                        <OIcon size={26} strokeWidth={2.5} />
                    </div>
                    <div className="flex-1">
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80 mb-1">
                            Comprobación de solvencia · LCSP Art. 87-90
                        </div>
                        <div className="font-display text-2xl md:text-3xl font-black leading-tight">
                            {oMeta.label}
                        </div>
                        {result.summary && (
                            <p className="text-sm font-medium opacity-95 mt-2 leading-snug">
                                {result.summary}
                            </p>
                        )}
                    </div>
                    <Button
                        onClick={run}
                        disabled={running}
                        variant="outline"
                        className="rounded-full h-9 px-4 bg-white/10 backdrop-blur hover:bg-white/20 border-white/30 text-white font-bold gap-1.5"
                        data-testid="solvency-match-rerun"
                    >
                        {running ? (
                            <Loader2 size={13} className="animate-spin" />
                        ) : (
                            <RefreshCw size={13} />
                        )}
                        Reanalizar
                    </Button>
                </div>
            </div>

            {/* Two sub-cards: economic + technical */}
            <div className="grid md:grid-cols-2 divide-x divide-ink-100">
                {["economic", "technical"].map((k) => {
                    const r = result[k] || {};
                    const m = metaOf(r.status);
                    const Icon = m.icon;
                    const label =
                        k === "economic" ? "Económico-financiera" : "Técnica";
                    return (
                        <div key={k} className="p-5">
                            <div className="flex items-center justify-between mb-3">
                                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-500">
                                    Solvencia {label}
                                </div>
                                <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${m.cls}`}>
                                    <Icon size={11} />
                                    {m.label}
                                </span>
                            </div>
                            {r.requirement && (
                                <div className="mb-2">
                                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 mb-0.5">
                                        Lo que pide el pliego
                                    </div>
                                    <div className="text-xs text-ink-700 font-medium leading-snug">
                                        {r.requirement}
                                    </div>
                                </div>
                            )}
                            {r.my_value && (
                                <div className="mb-2">
                                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 mb-0.5">
                                        Lo que aportas
                                    </div>
                                    <div className="text-xs text-ink-700 font-medium leading-snug">
                                        {r.my_value}
                                    </div>
                                </div>
                            )}
                            {r.message && (
                                <div className={`mt-3 p-3 rounded-xl text-xs font-medium leading-snug ${m.cls}`}>
                                    {r.message}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {result.checked_at && (
                <div className="px-5 py-2 bg-ink-50 text-[10px] font-mono text-ink-400 border-t border-ink-100">
                    Última comprobación: {result.checked_at.slice(0, 16).replace("T", " ")} UTC
                </div>
            )}
        </motion.div>
    );
}
